import { ReactNode } from "react"
import {
    Home, MapPin, BarChart3, Star, FileText, Calendar, Info,
    SlidersHorizontal, Download, TrendingUp,
} from "lucide-react"
import "./DesignSystemPage.css"

type Swatch = { name: string; varName: string; hex: string }

const GREENS: Swatch[] = [
    { name: "emerald-950", varName: "--hi-emerald-950", hex: "#003d31" },
    { name: "emerald-900", varName: "--hi-emerald-900", hex: "#004d3f" },
    { name: "emerald-800", varName: "--hi-emerald-800", hex: "#075b47" },
    { name: "emerald-700", varName: "--hi-emerald-700", hex: "#0b5a45" },
    { name: "emerald-600", varName: "--hi-emerald-600", hex: "#0e7a5f" },
    { name: "ink", varName: "--hi-ink", hex: "#14201c" },
]

const SURFACES: Swatch[] = [
    { name: "paper", varName: "--hi-paper", hex: "#fdf8ec" },
    { name: "sand-100", varName: "--hi-sand-100", hex: "#f4ead4" },
    { name: "sand-200", varName: "--hi-sand-200", hex: "#ece0c4" },
    { name: "sand-300", varName: "--hi-sand-300", hex: "#ddd0b0" },
]

const ACCENT_SEMANTIC: Swatch[] = [
    { name: "brass", varName: "--hi-brass", hex: "#b8924b" },
    { name: "brass-soft", varName: "--hi-brass-soft", hex: "#d4c4ad" },
    { name: "positive", varName: "--hi-positive", hex: "#2f8f5b" },
    { name: "negative", varName: "--hi-negative", hex: "#c0492f" },
    { name: "muted", varName: "--hi-muted", hex: "#5f6b66" },
]

const ICONS = [Home, MapPin, BarChart3, Star, FileText, Calendar, Info, SlidersHorizontal, Download, TrendingUp]

function SwatchGrid({ swatches }: { swatches: Swatch[] }) {
    return (
        <div className="ds-swatch-grid">
            {swatches.map((s) => (
                <div className="ds-swatch" key={s.varName}>
                    <div className="ds-swatch-chip" style={{ background: `var(${s.varName})` }} />
                    <div className="ds-swatch-meta">
                        <span className="ds-swatch-name">{s.name}</span>
                        <span className="ds-swatch-hex">{s.varName}</span>
                        <span className="ds-swatch-hex">{s.hex}</span>
                    </div>
                </div>
            ))}
        </div>
    )
}

const STATES = ["default", "hover", "pressed", "disabled"] as const

function ButtonStates({ variant, label, arrow }: { variant: string; label: string; arrow?: boolean }) {
    return (
        <div className="ds-state-grid">
            {STATES.map((st) => (
                <div key={st}>
                    <span className="ds-state-label">{st[0].toUpperCase() + st.slice(1)}</span>
                    <button
                        className={`ds-btn ds-btn--${variant} ds-btn--${st}`}
                        disabled={st === "disabled"}
                    >
                        {label}{arrow && <span className="ds-arrow"> →</span>}
                    </button>
                </div>
            ))}
        </div>
    )
}

function ConfRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="ds-val-row">
            <span className="ds-val-row-label">{label}</span>
            <span className="ds-val-row-value">{children}</span>
        </div>
    )
}

export default function DesignSystemPage() {
    return (
        <div className="ds-page">
            <div className="ds-shell">
                <h1 className="ds-title">HomeIndex design system</h1>
                <p className="ds-lede">
                    Canonical design tokens — "Emerald &amp; brass on warm sand".
                    Everything is rendered from the <code>--hi-*</code> variables in <code>css/Theme.css</code>.
                </p>

                <section className="ds-section">
                    <h2><span className="ds-num">01</span> Colour tokens</h2>
                    <p className="ds-subhead">Greens</p>
                    <SwatchGrid swatches={GREENS} />
                    <p className="ds-subhead">Surfaces — warm sand</p>
                    <SwatchGrid swatches={SURFACES} />
                    <p className="ds-subhead">Accent · semantic · muted</p>
                    <SwatchGrid swatches={ACCENT_SEMANTIC} />
                </section>

                <section className="ds-section">
                    <h2><span className="ds-num">02</span> Typography</h2>
                    <div className="ds-type-row">
                        <span className="ds-type-label">display-lg · 48 / 56 · Cormorant Garamond</span>
                        <p className="ds-display" style={{ fontSize: "var(--hi-display-lg)" }}>The right home.</p>
                    </div>
                    <div className="ds-type-row">
                        <span className="ds-type-label">display-md · 36 / 44 · Cormorant Garamond</span>
                        <p className="ds-display" style={{ fontSize: "var(--hi-display-md)" }}>€785,000 — HomeIndex Value</p>
                    </div>
                    <div className="ds-type-row">
                        <span className="ds-type-label">display-sm · 28 / 36 · Cormorant Garamond</span>
                        <p className="ds-display" style={{ fontSize: "var(--hi-display-sm)" }}>Property intelligence for buyers</p>
                    </div>
                    <div className="ds-type-row">
                        <span className="ds-type-label">xl · 22 / 30 · Inter Medium</span>
                        <p className="ds-body-sample" style={{ fontSize: "var(--hi-text-xl)", fontWeight: 500 }}>Local knowledge, confident decisions</p>
                    </div>
                    <div className="ds-type-row">
                        <span className="ds-type-label">base · 16 / 24 · Inter</span>
                        <p className="ds-body-sample" style={{ fontSize: "var(--hi-text-base)" }}>
                            Search any Irish address or area for instant property valuations, local market
                            signals, and buyer-ready insights.
                        </p>
                    </div>
                    <div className="ds-type-row">
                        <span className="ds-type-label">sm · 14 / 20 · Inter</span>
                        <p className="ds-body-sample" style={{ fontSize: "var(--hi-text-sm)", color: "var(--hi-muted)" }}>
                            Secondary copy and supporting detail.
                        </p>
                    </div>
                </section>

                <section className="ds-section">
                    <h2><span className="ds-num">03</span> Iconography</h2>
                    <div className="ds-icon-row">
                        {ICONS.map((LucideIcon, i) => (
                            <LucideIcon key={i} size={24} strokeWidth={2} aria-hidden />
                        ))}
                    </div>
                    <p className="ds-note">Lucide icons, 24px / 2px stroke, <code>currentColor</code>.</p>
                </section>

                <section className="ds-section">
                    <h2><span className="ds-num">04</span> Buttons &amp; chips</h2>

                    <p className="ds-subhead">Primary</p>
                    <ButtonStates variant="primary" label="Get valuation" />
                    <p className="ds-subhead">Secondary</p>
                    <ButtonStates variant="secondary" label="Explore areas" />
                    <p className="ds-subhead">Tertiary / text</p>
                    <ButtonStates variant="tertiary" label="Learn more" arrow />

                    <p className="ds-subhead">Chips</p>
                    <div className="ds-chip-rows">
                        <div className="ds-chip-line">
                            <span className="ds-chip-line-label">Filter</span>
                            <span className="ds-chip ds-chip--filter">Dublin <span className="ds-chip-x">×</span></span>
                            <span className="ds-chip ds-chip--filter">3 bed <span className="ds-chip-x">×</span></span>
                            <span className="ds-chip ds-chip--filter">Detached <span className="ds-chip-x">×</span></span>
                            <span className="ds-chip ds-chip--add">+ Add filter</span>
                        </div>
                        <div className="ds-chip-line">
                            <span className="ds-chip-line-label">Status</span>
                            <span className="ds-chip ds-chip--featured"><Star size={13} strokeWidth={2} /> Featured</span>
                            <span className="ds-chip ds-chip--new"><span className="ds-dot ds-dot--pos" /> New</span>
                            <span className="ds-chip ds-chip--neg">↓ Price drop</span>
                            <span className="ds-chip ds-chip--muted">○ Sold</span>
                        </div>
                        <div className="ds-chip-line">
                            <span className="ds-chip-line-label">Metric</span>
                            <span className="ds-chip ds-chip--pos">↑ 3.2%</span>
                            <span className="ds-chip ds-chip--neg">↓ 1.1%</span>
                            <span className="ds-chip ds-chip--muted">Neutral</span>
                        </div>
                    </div>
                </section>

                <section className="ds-section">
                    <h2><span className="ds-num">05</span> Applied UI examples</h2>

                    <p className="ds-subhead">Hero search</p>
                    <div className="ds-hero">
                        <div className="ds-hero-panel">
                            <p className="ds-display" style={{ fontSize: "var(--hi-display-sm)", color: "var(--hi-text-on-dark)" }}>
                                Find your next,<br />with confidence.
                            </p>
                            <p className="ds-body-sample" style={{ fontSize: "var(--hi-text-sm)", color: "var(--hi-brass-soft)" }}>
                                Search any address or area in Ireland.
                            </p>
                        </div>
                        <div className="ds-hero-search">
                            <span className="ds-input" aria-hidden>e.g. Rathmines, Dublin 6</span>
                            <span className="ds-hero-pin" aria-hidden><MapPin size={18} strokeWidth={2} /></span>
                            <button className="ds-btn ds-btn--primary ds-btn--default">Get valuation</button>
                        </div>
                    </div>

                    <p className="ds-subhead">Valuation card</p>
                    <div className="ds-val">
                        <div className="ds-val-panel">
                            <p className="ds-card-label">HOMEINDEX VALUE</p>
                            <p className="ds-card-value">€785,000</p>
                            <p className="ds-body-sample" style={{ fontSize: "var(--hi-text-sm)", color: "var(--hi-text-on-dark)" }}>
                                €745k – €825k <span style={{ color: "var(--hi-brass-soft)" }}>↑ 3.2%</span>
                            </p>
                        </div>
                        <div className="ds-val-body">
                            <ConfRow label="Confidence">
                                <span className="ds-conf-track"><span className="ds-conf-fill" style={{ width: "80%" }} /></span> High
                            </ConfRow>
                            <ConfRow label="Market trend (3m)"><span style={{ color: "var(--hi-positive)" }}>↑ 3.2%</span></ConfRow>
                            <ConfRow label="Comparable sales">24</ConfRow>
                            <div className="ds-chip-line" style={{ marginTop: "var(--hi-space-3)" }}>
                                <span className="ds-chip ds-chip--featured"><Star size={13} strokeWidth={2} /> Featured</span>
                                <span className="ds-chip ds-chip--new"><span className="ds-dot ds-dot--pos" /> New</span>
                            </div>
                        </div>
                    </div>

                    <p className="ds-subhead">Filter row</p>
                    <div className="ds-filter-row">
                        <span className="ds-select">Dublin <span className="ds-caret">▾</span></span>
                        <span className="ds-select">3 bed <span className="ds-caret">▾</span></span>
                        <span className="ds-select">Detached <span className="ds-caret">▾</span></span>
                        <span className="ds-select">Price: Any <span className="ds-caret">▾</span></span>
                        <button className="ds-btn ds-btn--secondary ds-btn--default"><SlidersHorizontal size={16} strokeWidth={2} /> More filters</button>
                        <button className="ds-btn ds-btn--tertiary ds-btn--default">Clear all</button>
                    </div>
                </section>
            </div>
        </div>
    )
}
